import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Banknote, 
  Building2, 
  Truck, 
  Wrench,
  ShoppingCart,
  Loader2,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { t } = useTranslation();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const { items, getSubtotal, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('interac');
  const [deliveryRequested, setDeliveryRequested] = useState(false);
  const [installationRequested, setInstallationRequested] = useState(false);
  const [address, setAddress] = useState({
    street: '',
    city: '',
    province: 'QC',
    postalCode: '',
    country: 'Canada'
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
    }
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [isAuthenticated, items, authLoading]);

  const subtotal = getSubtotal();
  const deliveryFee = deliveryRequested ? 50 : 0;
  const installationFee = installationRequested ? 100 : 0;
  const taxableAmount = subtotal + deliveryFee + installationFee;
  const taxes = taxableAmount * 0.14975;
  const total = taxableAmount + taxes;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!address.street || !address.city || !address.postalCode) {
      toast.error('Veuillez remplir l\'adresse de livraison');
      return;
    }

    try {
      setLoading(true);
      
      const orderData = {
        items: items.map(item => ({
          product_id: item.product.id,
          product_name: item.product.name_fr,
          quantity: item.quantity,
          unit_price: item.product.price,
          total_price: item.product.price * item.quantity
        })),
        shipping_address: address,
        delivery_requested: deliveryRequested,
        installation_requested: installationRequested,
        payment_method: paymentMethod
      };

      await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      clearCart();
      toast.success('Commande créée avec succès!');
      navigate('/account');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || items.length === 0) return null;

  return (
    <div className="min-h-screen py-12 bg-gray-50" data-testid="checkout-page">
      <div className="container mx-auto px-4">
        <Link to="/cart" className="inline-flex items-center text-gray-500 hover:text-black mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au panier
        </Link>

        <h1 className="text-3xl font-bold text-black mb-8">Finaliser la commande</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Address */}
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Adresse de livraison
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Adresse</Label>
                    <Input
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      className="bg-white border-gray-300 text-black"
                      placeholder="123 rue Principale"
                      data-testid="address-street"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Ville</Label>
                      <Input
                        value={address.city}
                        onChange={(e) => setAddress({ ...address, city: e.target.value })}
                        className="bg-white border-gray-300 text-black"
                        placeholder="Montréal"
                        data-testid="address-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Code postal</Label>
                      <Input
                        value={address.postalCode}
                        onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                        className="bg-white border-gray-300 text-black"
                        placeholder="H2X 1Y4"
                        data-testid="address-postal"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Options */}
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Options supplémentaires</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Truck className="h-5 w-5 text-gray-700" />
                      <div>
                        <p className="text-black font-medium">Livraison</p>
                        <p className="text-gray-500 text-sm">Service de livraison à votre adresse</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-black font-mono">{formatPrice(50)}</span>
                      <Checkbox
                        checked={deliveryRequested}
                        onCheckedChange={setDeliveryRequested}
                        data-testid="delivery-checkbox"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Wrench className="h-5 w-5 text-gray-700" />
                      <div>
                        <p className="text-black font-medium">Installation</p>
                        <p className="text-gray-500 text-sm">Installation professionnelle incluse</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-black font-mono">{formatPrice(100)}</span>
                      <Checkbox
                        checked={installationRequested}
                        onCheckedChange={setInstallationRequested}
                        data-testid="installation-checkbox"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Méthode de paiement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                    <div className={`flex items-center justify-between p-4 rounded-lg border ${paymentMethod === 'interac' ? 'border-black bg-gray-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="interac" id="interac" data-testid="payment-interac" />
                        <Label htmlFor="interac" className="flex items-center gap-2 cursor-pointer">
                          <Banknote className="h-5 w-5 text-gray-700" />
                          <span className="text-black">Virement Interac</span>
                        </Label>
                      </div>
                    </div>

                    <div className={`flex items-center justify-between p-4 rounded-lg border ${paymentMethod === 'bank_transfer' ? 'border-black bg-gray-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="bank_transfer" id="bank_transfer" data-testid="payment-bank" />
                        <Label htmlFor="bank_transfer" className="flex items-center gap-2 cursor-pointer">
                          <Building2 className="h-5 w-5 text-gray-700" />
                          <span className="text-black">Virement bancaire</span>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>

                  {paymentMethod === 'interac' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="interac-instructions">
                      <p className="text-blue-900 font-semibold text-sm">Instructions de virement Interac</p>
                      <p className="text-blue-800 text-sm">
                        Après confirmation de votre commande, veuillez effectuer un virement Interac à l'adresse suivante :
                      </p>
                      <div className="bg-white border border-blue-200 rounded p-3 font-mono text-sm text-black">
                        facture@raq-canada.com
                      </div>
                      <p className="text-blue-700 text-xs">
                        Indiquez votre numéro de commande dans la note du virement. Votre commande sera traitée dès réception du paiement.
                      </p>
                    </div>
                  )}

                  {paymentMethod === 'bank_transfer' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="bank-instructions">
                      <p className="text-blue-900 font-semibold text-sm">Virement bancaire</p>
                      <p className="text-blue-800 text-sm">
                        Vous serez contacté(e) par notre équipe à l'adresse <span className="font-mono">{user?.email}</span> afin de recevoir notre spécimen de chèque contenant nos coordonnées bancaires complètes.
                      </p>
                      <p className="text-blue-700 text-xs">
                        Votre commande sera confirmée dès réception du virement.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Summary */}
            <div>
              <Card className="bg-white border-gray-200 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-black flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Résumé
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex justify-between text-sm">
                      <span className="text-gray-700">{item.product.name_fr} x{item.quantity}</span>
                      <span className="text-gray-500 font-mono">{formatPrice(item.product.price * item.quantity)}</span>
                    </div>
                  ))}

                  <Separator className="bg-gray-200" />

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-gray-700">
                      <span>Sous-total</span>
                      <span className="font-mono">{formatPrice(subtotal)}</span>
                    </div>
                    {deliveryRequested && (
                      <div className="flex justify-between text-gray-700">
                        <span>Livraison</span>
                        <span className="font-mono">{formatPrice(deliveryFee)}</span>
                      </div>
                    )}
                    {installationRequested && (
                      <div className="flex justify-between text-gray-700">
                        <span>Installation</span>
                        <span className="font-mono">{formatPrice(installationFee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-700">
                      <span>Taxes (TPS+TVQ)</span>
                      <span className="font-mono">{formatPrice(taxes)}</span>
                    </div>
                  </div>

                  <Separator className="bg-gray-200" />

                  <div className="flex justify-between text-lg font-semibold text-black">
                    <span>Total</span>
                    <span className="font-mono">{formatPrice(total)}</span>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black text-white hover:bg-gray-800"
                    size="lg"
                    data-testid="place-order-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Confirmer la commande
                  </Button>

                  <p className="text-xs text-gray-400 text-center">
                    En confirmant, vous acceptez nos conditions générales de vente
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
